import { inject, Injectable, Injector } from '@angular/core';
import { ComponentPortal } from '@angular/cdk/portal';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SecurityService } from '../../../services/security.service';
import { NotificationsPreferencesService } from '../../../services/notifications/notifications-preferences.service';
import { NotificationsPreferencesType } from '../../../services/notifications/notifications-preferences.domain';
import { WebsocketService } from '../../../services/websocket/websocket.service';
import { PreferencesService } from '../../../services/preferences/preferences.service';
import { WebsocketData, WebsocketMessage, WebsocketMessageType } from '../../../services/websocket/websocket.domain';
import { NOTIFICATIONS_TOAST_CONFIGURATION, NotificationsToastContainerComponent } from '../notifications-toast-container.component';
import { NotificationOverlayRef, NotificationsToast, NotificationsToastConfiguration } from '../notifications-toast.domain';
import { NotificationsOverlayService } from './notifications-overlay.service';

/**
 * Notifications Toast Service subscribe to websocket
 * Is iresposibale for creating and destroying NotificationsToastContainerComponent
 *
 * Acutal toast need to set by delegate function 'setToast'
 *
 */

@Injectable({
  providedIn: 'root'
})
export class NotificationsToastService {

  private static readonly OVERLAY_MARGIN: string = '20px';

  // Emit filtered notifications received from websocket
  readonly onNotificationsReceived: Subject<WebsocketMessage<NotificationsToast>>;

  // Subscribed by toast component to get toast data
  readonly toastData: Subject<NotificationsToast>;

  private permission: boolean = false;
  private isInitialized: boolean = false;
  private preferences: NotificationsPreferencesType;
  private messageSubscription: Subscription;
  private preferencesSubscription: Subscription;
  private overlayRef: NotificationOverlayRef;

  private readonly notificationsPreferencesService: NotificationsPreferencesService = inject(NotificationsPreferencesService);
  private readonly securityService: SecurityService = inject(SecurityService);
  private readonly injector: Injector = inject(Injector);
  private readonly websocketService: WebsocketService = inject(WebsocketService);
  private readonly notificationOverlayService: NotificationsOverlayService = inject(NotificationsOverlayService);
  private readonly preferenceService: PreferencesService = inject(PreferencesService);

  constructor() {
    this.onNotificationsReceived = new Subject<WebsocketMessage<NotificationsToast>>();
    this.toastData = new Subject<NotificationsToast>();
  }

  /**
   * Initialized notifications
   * create websocket connection if required
   */
  init(): void {
    this.permission = this.securityService.hasPermission('FILL_NOTIFICATIONS');
    if (this.permission === true) {
      if (this.isInitialized === false) {
        this.preferenceService.onPreferenceValueChange.subscribe((): void => {
          this.getNotificationsPreferences();
        });
        this.getNotificationsPreferences();

        this.notificationOverlayService.onClose
          .subscribe((): void => {
            this.overlayRef = undefined;
          });
      }
      this.isInitialized = true;
    }
  }

  /**
   * Delegate method to set toast component data
   */
  setToast(toast: NotificationsToast): void {
    if (this.permission) {
      if (this.overlayRef === undefined) {
        this.overlayRef = this.notificationOverlayService.open({
          notificationLocation: this.preferences.notificationLocation,
          margin: NotificationsToastService.OVERLAY_MARGIN,
          containerPortal: this.getNotificationOverlayConfig()
        });
      }
      this.toastData.next(toast);
    }
  }

  private getNotificationsPreferences(): void {
    this.preferencesSubscription?.unsubscribe();
    this.preferencesSubscription = this.notificationsPreferencesService.getNotificationsPreferences()
      .subscribe((preferences: NotificationsPreferencesType): void => {
        this.onPreferencesChange(preferences);
      });
  }

  /**
   * Notification prefernces changes
   */
  private onPreferencesChange(preferences: NotificationsPreferencesType): void {
    this.preferences = preferences;
    if (this.preferences.showNotificationStatus === undefined || this.preferences.showNotificationStatus.length === 0) {
      this.messageSubscription?.unsubscribe();
      this.notificationOverlayService.close(this.overlayRef);
      this.overlayRef = undefined;
    } else {
      this.connectWebsocket();
    }
  }

  /**
   * Create websocket connection and filter notifications based on type
   */
  private connectWebsocket(): void {
    this.messageSubscription?.unsubscribe();
    this.messageSubscription = this.websocketService.onMessageReceived
      .pipe(filter((message: WebsocketMessage<WebsocketData>): boolean => message.type === WebsocketMessageType.ORDER_STATUS)
      )
      .subscribe((message: WebsocketMessage<WebsocketData>): void => {
        this.onNotificationsReceived.next(message);
      });
    this.websocketService.connect();
  }

  private getNotificationOverlayConfig(): ComponentPortal<NotificationsToastContainerComponent> {
    const data: NotificationsToastConfiguration = {
      preferences: this.preferences,
      notifications: this.toastData
    };
    const injector = Injector.create({
      providers: [{ provide: NOTIFICATIONS_TOAST_CONFIGURATION, useValue: data }],
      parent: this.injector
    });
    return new ComponentPortal(
      NotificationsToastContainerComponent,
      null,
      injector
    );
  }
}
